import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { closeDownloadModal, fetchAvailability } from './actions';
import scrollIntoView from 'scroll-into-view';
import Header from './Header';
import Avatar from './Avatar';
import ProductList from './ProductList';
import DownloadModal from './DownloadModal';
import { AccentH4, H1Alt, Body } from 'scy/text';
import { mutedAccent, tablet_h2Alt } from 'scy/text/styles.mod.scss';
import { TextButton, BUTTON_CLASSES } from 'scy/buttons';
import { textLeft, tablet_textCenter } from 'scy/utils.mod.scss';
import LoadingSpinner from 'scy/LoadingSpinner';
import ChevronDown from 'images/icons/controls/chevron-down.svg?react-svg';
import { tablet_hidden } from 'scy/display.mod.scss';
import padding from 'scy/padding.mod.scss';
import margin from 'scy/margin.mod.scss';
import {
  container,
  messageSection,
  heading,
  quote,
  productSection,
  ctaSection,
  disclaimer
} from './LandingPage.mod.scss';
import { cta } from './styles.mod.scss';
import { isScheduledOrder } from 'shared/models/order';

export default function GiftClaimLandingPage({ wherePath, confirmPath }) {
  const dispatch = useDispatch();
  const {
    order,
    showDownloadModal,
    availabilityLoading,
    date,
    slot,
    selectedETA
  } = useSelector(state => state.giftClaim);
  const customer = order ? order.customer : null;
  const firstName = customer.first_name || '';
  const message = order && order.gift_info ? order.gift_info.message : '';
  const fromName = [
    firstName,
    customer.last_name ? `${customer.last_name[0]}.` : ''
  ].join(' ');
  const hasConfirmed = !!order && isScheduledOrder(order);

  const productListSection = useRef();

  function scrollToProducts(e) {
    e.preventDefault();
    if (productListSection.current) {
      scrollIntoView(productListSection.current, {
        align: { top: 0, topOffset: 16 }
      });
    }
  }

  useEffect(() => {
    if (hasConfirmed && !selectedETA) {
      dispatch(fetchAvailability());
    }
  }, [dispatch, hasConfirmed, selectedETA]);

  return (
    <>
      <Header />
      <main className={container}>
        <section className={messageSection}>
          <Avatar user={customer} />
          <Heading fromName={fromName} />
          <MessageBlock message={message} fromName={fromName} />
          <ScrollButton onClick={scrollToProducts} firstName={firstName} />
        </section>

        <section ref={productListSection} className={productSection}>
          <ProductList products={order.products} />
        </section>

        <section className={ctaSection}>
          <Link
            to={hasConfirmed ? confirmPath : wherePath}
            className={`${BUTTON_CLASSES.primaryRect} ${cta}`}
          >
            {hasConfirmed && availabilityLoading ? (
              <LoadingSpinner />
            ) : (
              <>
                <div>
                  {hasConfirmed ? (
                    <DeliveryETA
                      date={date}
                      slot={slot}
                      selectedETA={selectedETA}
                    />
                  ) : (
                    'Schedule Gift Delivery'
                  )}
                </div>
                <div className={disclaimer}>
                  <small>Valid I.D. required at time of delivery</small>
                </div>
              </>
            )}
          </Link>
        </section>
      </main>

      {!!showDownloadModal && (
        <DownloadModal
          isOpen={showDownloadModal}
          handleClose={() => dispatch(closeDownloadModal())}
        />
      )}
    </>
  );
}

function Heading({ fromName }) {
  return (
    <AccentH4
      tag="h1"
      marginClass={`${margin.top_0} ${margin.bottom_md}`}
      className={heading}
    >
      You just got a gift on Saucey{fromName ? ` from ${fromName}` : ''}
    </AccentH4>
  );
}

function MessageBlock({ message, fromName }) {
  if (!message) return null;

  return (
    <blockquote className={quote}>
      <H1Alt
        tag="p"
        className={`${mutedAccent} ${textLeft} ${tablet_textCenter} ${tablet_h2Alt}`}
        marginClass={`${margin.top_0} ${margin.bottom_sm}`}
      >
        “{message}”
      </H1Alt>
      {!!fromName && (
        <Body tag="footer" className={`${mutedAccent} ${padding.xs}`}>
          - {fromName}
        </Body>
      )}
    </blockquote>
  );
}

function ScrollButton({ onClick, firstName }) {
  return (
    <TextButton
      noUnderline
      onClick={onClick}
      className={`${padding.xs} ${tablet_hidden}`}
    >
      <AccentH4 tag="h2">
        <div className={margin.bottom_xs}>
          See what {firstName || 'they'} sent you.
        </div>
        <div className={padding.xxs}>
          <ChevronDown />
        </div>
      </AccentH4>
    </TextButton>
  );
}

function DeliveryETA({ date, slot, selectedETA }) {
  return date && slot
    ? `Delivering ${moment(date.date).format('MMM D')} ${slot.description}`
    : `Delivering ASAP${
        selectedETA ? ` (Est. ${selectedETA.replace('minutes', 'min')})` : ''
      }`;
}
